import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { setLoginPopupOpen } from '../../store/slices/uiSlice'
import GoogleOAuthButton from '../auth/GoogleOAuthButton'
import EmailInput from '../ui/EmailInput/EmailInput'
import PhoneNumber, { Country } from '../ui/PhoneNumber/PhoneNumber'
import CodeInput from './CodeInput'
import { CloseIcon, MailIcon, PhoneIcon, RightArrowIcon, SeatersIllustration } from './icons'
import styles from './LoginPopup.module.scss'

const defaultCountry: Country = {
  code: 'UA',
  name: 'Україна',
  flag: '🇺🇦',
  phoneCode: '+380',
  pattern: /^(\+380|380)?[0-9]{9}$/,
  placeholder: 'Номер телефону',
  maxLength: 9,
}

interface LoginPopupProps {
  isOpen: boolean
  onClose: () => void
}

const LoginPopup: React.FC<LoginPopupProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    customAuthInitiate,
    customAuthChallenge,
    customAuthOTP,
    otpSignUp,
    otpConfirmSignUp,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth()

  // Note: Authentication state is managed by AuthContext

  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showCodeInput, setShowCodeInput] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<Country>(defaultCountry)
  const [phoneError, setPhoneError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [isEmailMode, setIsEmailMode] = useState(false)
  const [authStep, setAuthStep] = useState<'initiate' | 'otp' | 'signup' | 'confirm-signup'>(
    'initiate',
  )
  const [session, setSession] = useState('')
  const [generatedUsername, setGeneratedUsername] = useState('')
  const [error, setError] = useState('')

  // Track if we've already handled OAuth success to prevent infinite loops
  const oauthSuccessHandled = useRef(false)

  // Memoize the reset function to prevent unnecessary re-renders
  const resetPopupState = useCallback(() => {
    setPhoneNumber('')
    setEmail('')
    setIsLoading(false)
    setShowCodeInput(false)
    setSelectedCountry(defaultCountry)
    setPhoneError('')
    setEmailError('')
    setIsEmailMode(false)
    setAuthStep('initiate')
    setSession('')
    setGeneratedUsername('')
    setError('')
  }, [])

  // Handle OAuth redirect success
  useEffect(() => {
    if (isAuthenticated && !authLoading && !oauthSuccessHandled.current) {
      oauthSuccessHandled.current = true

      resetPopupState()
      dispatch(setLoginPopupOpen(false))
      onClose()
    }
  }, [isAuthenticated, authLoading, dispatch, navigate, onClose, resetPopupState])

  // Reset OAuth success flag when popup opens
  useEffect(() => {
    if (isOpen) {
      oauthSuccessHandled.current = false
    }
  }, [isOpen])

  // Handle OAuth errors from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const oauthError = urlParams.get('error')
    const oauthErrorDescription = urlParams.get('error_description')

    if (oauthError && isOpen) {
      // OAuth error

      // Handle "already signed in" error by checking for existing user
      if (
        oauthErrorDescription?.includes('already a signed in user') ||
        oauthErrorDescription?.includes('already signed in')
      ) {
        // Try to get the current user from Cognito
        const checkExistingUser = async () => {
          try {
            const { getCurrentUser } = await import('aws-amplify/auth')
            await getCurrentUser()

            // If we found an existing user, close the popup and let the auth system handle it
            resetPopupState()
            dispatch(setLoginPopupOpen(false))
            onClose()
            // Перенаправляємо на сторінку особистого кабінету
            navigate('/account/orders')
            return
          } catch (err) {
            setError('Please sign out first before signing in with a different account')
          }
        }

        checkExistingUser()
      } else {
        setError(oauthErrorDescription || 'OAuth authentication failed')
      }

      // Clean up URL parameters
      const newUrl = window.location.origin + window.location.pathname
      window.history.replaceState({}, document.title, newUrl)
    }
  }, [isOpen, resetPopupState, dispatch, onClose, navigate, location.state?.from?.pathname])

  const handleClose = () => {
    resetPopupState()
    dispatch(setLoginPopupOpen(false))
    onClose()
  }

  const validatePhoneNumber = (phone: string, country: Country): string => {
    if (!phone.trim()) {
      return 'Введіть номер телефону'
    }

    // Видаляємо всі нецифрові символи крім +
    const cleanPhone = phone.replace(/[^\d+]/g, '')

    // Перевіряємо за патерном країни
    if (!country.pattern.test(cleanPhone)) {
      switch (country.code) {
        case 'UA':
          return 'Перевірте правильність номеру'
        case 'PL':
          return 'Перевірте правильність номеру'
        case 'DE':
          return 'Перевірте правильність номеру'
        default:
          return 'Перевірте правильність номеру'
      }
    }

    return ''
  }

  const validateEmail = (email: string): string => {
    if (!email.trim()) {
      return 'Введіть email'
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return 'Перевірте правильність email'
    }

    return ''
  }

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value)

    // Очищаємо помилку при зміні
    if (phoneError) {
      setPhoneError('')
    }
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)

    // Очищаємо помилку при зміні
    if (emailError) {
      setEmailError('')
    }
  }

  // Перевіряємо чи валідний номер для активації кнопки
  const isPhoneValid = () => {
    if (!phoneNumber.trim()) return false
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, '')
    return selectedCountry.pattern.test(cleanPhone)
  }

  // Перевіряємо чи валідний email для активації кнопки
  const isEmailValid = () => {
    if (!email.trim()) return false
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Загальна валідація залежно від режиму
  const isFormValid = () => {
    return isEmailMode ? isEmailValid() : isPhoneValid()
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const username = isEmailMode ? email : `${selectedCountry.phoneCode}${phoneNumber}`

    if (isEmailMode) {
      const error = validateEmail(email)
      if (error) {
        setEmailError(error)
        return
      }
      setEmailError('')
    } else {
      const error = validatePhoneNumber(phoneNumber, selectedCountry)
      if (error) {
        setPhoneError(error)
        return
      }
      setPhoneError('')
    }

    setIsLoading(true)

    try {
      // First try Custom Auth flow for existing users (as per instructions)
      const customAuthResult = await customAuthInitiate(username)

      if (customAuthResult.success) {
        // User exists, proceed with custom auth flow

        // Step 2: Respond to challenge with delivery medium
        const deliveryMedium = isEmailMode ? 'email' : 'phone_number'

        const challengeResult = await customAuthChallenge(
          customAuthResult.data.Session,
          deliveryMedium,
          username,
        )

        if (challengeResult.success) {
          setSession(challengeResult.data.Session)
          setAuthStep('otp')
          setShowCodeInput(true)
        } else {
          setError(challengeResult.error || 'Authentication challenge failed')
        }
      } else {
        // Check if user doesn't exist, then try sign up

        // Check for various ways AWS might indicate user doesn't exist
        const isUserNotFound =
          customAuthResult.error?.includes('UserNotFoundException') ||
          customAuthResult.error?.includes('NotAuthorizedException') ||
          customAuthResult.error?.includes('InvalidParameterException') ||
          customAuthResult.error?.includes('User does not exist') ||
          customAuthResult.error?.includes('User not found') ||
          customAuthResult.error?.includes('Invalid username') ||
          customAuthResult.error?.includes('Username/client id combination not found')

        if (isUserNotFound) {
          // User doesn't exist, try OTP sign up flow
          const signUpResult = await otpSignUp(username)

          if (signUpResult.success) {
            setGeneratedUsername(signUpResult.data.generatedUsername)
            setSession(signUpResult.data.session)
            setAuthStep('confirm-signup')
            setShowCodeInput(true)
          } else {
            setError(signUpResult.error || 'Sign up failed')
          }
        } else {
          setError(customAuthResult.error || 'Authentication failed')
        }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeSubmit = async (code: string) => {
    setError('')
    setIsLoading(true)

    try {
      if (authStep === 'confirm-signup') {
        // Confirm sign up with OTP
        const username = isEmailMode ? email : `${selectedCountry.phoneCode}${phoneNumber}`
        const result = await otpConfirmSignUp(generatedUsername, code, session, username)

        if (result.success) {
          resetPopupState()
          dispatch(setLoginPopupOpen(false))
          onClose()
          // Перенаправляємо на сторінку особистого кабінету
          navigate('/account/orders')
        } else {
          setError(result.error || 'Confirmation failed')
        }
      } else if (authStep === 'otp') {
        // Custom auth flow - step 3: respond to OTP challenge
        const username = isEmailMode ? email : `${selectedCountry.phoneCode}${phoneNumber}`
        const result = await customAuthOTP(session, code, username)

        if (result.success) {
          resetPopupState()
          dispatch(setLoginPopupOpen(false))
          onClose()
          // Перенаправляємо на сторінку особистого кабінету
          navigate('/account/orders')
        } else {
          setError(result.error || 'Authentication failed')
        }
      } else {
        setError('Invalid authentication step')
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackFromCode = () => {
    setShowCodeInput(false)
    setPhoneNumber('')
    setPhoneError('')
  }

  const handleResendCode = async () => {
    setError('')
    setIsLoading(true)

    try {
      const username = isEmailMode ? email : `${selectedCountry.phoneCode}${phoneNumber}`

      if (authStep === 'confirm-signup') {
        // Resend sign up code
        const signUpResult = await otpSignUp(username)

        if (signUpResult.success) {
          setGeneratedUsername(signUpResult.data.generatedUsername)
          setSession(signUpResult.data.session)
          // Keep the same auth step
        } else {
          setError(signUpResult.error || 'Failed to resend sign up code')
        }
      } else if (authStep === 'otp') {
        // Resend OTP for existing user
        const customAuthResult = await customAuthInitiate(username)

        if (customAuthResult.success) {
          // Step 2: Respond to challenge with delivery medium
          const deliveryMedium = isEmailMode ? 'email' : 'phone_number'

          const challengeResult = await customAuthChallenge(
            customAuthResult.data.Session,
            deliveryMedium,
            username,
          )

          if (challengeResult.success) {
            setSession(challengeResult.data.Session)
            // Keep the same auth step
          } else {
            setError(challengeResult.error || 'Failed to resend OTP code')
          }
        } else {
          setError(customAuthResult.error || 'Failed to resend OTP code')
        }
      } else {
        setError('Invalid authentication step for resend')
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred while resending code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country)
    setPhoneNumber('')
    setPhoneError('')
  }

  const handlePhoneBlur = () => {
    if (phoneNumber.trim()) {
      const error = validatePhoneNumber(phoneNumber, selectedCountry)
      setPhoneError(error)
    }
  }

  const handleEmailBlur = () => {
    if (email.trim()) {
      const error = validateEmail(email)
      setEmailError(error)
    }
  }

  const handleEmailClick = () => {
    setIsEmailMode(true)
    setPhoneNumber('')
    setPhoneError('')
    setEmail('')
    setEmailError('')
  }

  const handleBackToPhone = () => {
    setIsEmailMode(false)
    setEmail('')
    setEmailError('')
    setPhoneNumber('')
    setPhoneError('')
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      resetPopupState()
      dispatch(setLoginPopupOpen(false))
      onClose()
    }
  }

  // Анімації для overlay
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.3 },
    },
  }

  // Анімації для popup
  const popupVariants = {
    hidden: {
      opacity: 0,
      x: '100%',
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      x: '100%',
      transition: {
        duration: 0.3,
      },
    },
  }

  // Анімації для CodeInput
  const codeInputVariants = {
    hidden: {
      opacity: 0,
      x: '100%',
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      x: '100%',
      transition: {
        duration: 0.3,
      },
    },
  }

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className={styles.overlay}
          onClick={handleOverlayClick}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {showCodeInput ? (
            <motion.div
              className={styles.codeContainer}
              variants={codeInputVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <CodeInput
                onBack={handleBackFromCode}
                phoneNumber={isEmailMode ? email : `${selectedCountry.phoneCode} ${phoneNumber}`}
                onCodeSubmit={handleCodeSubmit}
                onResendCode={handleResendCode}
                isEmail={isEmailMode}
                digits={authStep === 'otp' ? 5 : 6}
              />
            </motion.div>
          ) : (
            <motion.div
              className={styles.popup}
              variants={popupVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className={styles.content}>
                <div className={styles.header}>
                  <h2 className={styles.headerTitle}>Вхід</h2>
                  <button className={styles.closeButton} onClick={handleClose}>
                    <CloseIcon />
                  </button>
                </div>

                <motion.div
                  className={styles.wrapper}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                >
                  <h1 className={styles.title}>Раді бачити тебе знову!</h1>

                  <form className={styles.form} onSubmit={handleFormSubmit}>
                    <div className={styles.inputGroup}>
                      {isEmailMode ? (
                        <EmailInput
                          value={email}
                          onChange={handleEmailChange}
                          error={emailError}
                          placeholder="Email"
                          label="Email"
                          onBlur={handleEmailBlur}
                        />
                      ) : (
                        <PhoneNumber
                          value={phoneNumber}
                          onChange={handlePhoneChange}
                          onCountryChange={handleCountryChange}
                          selectedCountry={selectedCountry}
                          error={phoneError}
                          placeholder={selectedCountry.placeholder}
                          label="Номер телефону"
                          onBlur={handlePhoneBlur}
                        />
                      )}
                    </div>

                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={isLoading || !isFormValid()}
                    >
                      <RightArrowIcon className={styles.submitIcon} />
                      {isLoading ? 'Вхід...' : 'Вхід'}
                    </button>
                  </form>

                  <div className={styles.divider}>
                    <div className={styles.dividerLine}></div>
                    <span className={styles.dividerText}>або</span>
                    <div className={styles.dividerLine}></div>
                  </div>

                  <button
                    className={styles.emailButton}
                    onClick={isEmailMode ? handleBackToPhone : handleEmailClick}
                  >
                    {isEmailMode ? (
                      <PhoneIcon className={styles.emailIcon} />
                    ) : (
                      <MailIcon className={styles.emailIcon} />
                    )}

                    {isEmailMode ? 'Продовжити з телефоном' : 'Продовжити з email'}
                  </button>

                  <div className={styles.divider}>
                    <div className={styles.dividerLine}></div>
                    <span className={styles.dividerText}>або</span>
                    <div className={styles.dividerLine}></div>
                  </div>

                  <GoogleOAuthButton
                    onSuccess={() => {
                      // OAuth redirect will handle success
                    }}
                    onError={error => {
                      setError(error)
                    }}
                  />
                </motion.div>
              </div>
              <motion.div
                className={styles.illustration}
                initial={{ opacity: 0, y: -50, x: -50, rotate: -5 }}
                animate={{ opacity: 1, y: 0, x: 0, rotate: 0 }}
                transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
              >
                <SeatersIllustration />
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default LoginPopup
